/**
 * Fallback Translation Repository
 * Fetches translations from API, falls back to local files on error
 * 
 * Primary: Fetches from API via getAllTranslationsUseCase
 * Fallback: Uses LocalTranslationRepository if API fails
 */

import { servicesContainer } from '@/core/di/services-container';
import { ApplicationType, TranslationKey } from '@/features/translations/types/translation-types';
import type { IAppTranslationRepository } from './i18n-repository.interface';
import type { TranslationResources, Language } from '../types/i18n-types';
import { LocalTranslationRepository } from './local-translation-repository';
import { useI18nStore } from '../stores/i18n-store';
import { SUPPORTED_LANGUAGES } from '../types/i18n-types';
import { useAuthStore } from '@/core/stores/auth-store';

/**
 * Transform API translation keys to i18next resource format
 * 
 * API format:
 * [
 *   { keyName: "key1", values: [{ languageCode: "en", value: "Hello" }, { languageCode: "he", value: "שלום" }] },
 *   { keyName: "key2", values: [{ languageCode: "en", value: "World" }, { languageCode: "he", value: "עולם" }] }
 * ]
 * 
 * i18next format:
 * {
 *   "en": { "key1": "Hello", "key2": "World" },
 *   "he": { "key1": "שלום", "key2": "עולם" }
 * }
 */
function transformToI18nResources(
  translationKeys: TranslationKey[]
): Record<string, Record<string, string>> {
  const resources: Record<string, Record<string, string>> = {};

  for (const key of translationKeys) {
    if (!key.isActive) continue;

    for (const value of key.values) {
      const langCode = value.languageCode.toLowerCase();

      if (!resources[langCode]) {
        resources[langCode] = {};
      }

      resources[langCode][key.keyName] = value.value;
    }
  }

  return resources;
}

/**
 * Fallback Translation Repository
 * Primary: Fetches from API via getAllTranslationsUseCase
 * Fallback: Uses LocalTranslationRepository if API fails
 */
export class FallbackTranslationRepository implements IAppTranslationRepository {
  private localRepo: LocalTranslationRepository;
  private fetchPromise: Promise<Record<string, Record<string, string>>> | null = null;

  constructor() {
    this.localRepo = new LocalTranslationRepository();
  }

  /**
   * Load translations for a specific language and namespace
   * Fetches from API (fresh on every call), falls back to local on error
   */
  async loadTranslations(language: string, namespace: string): Promise<TranslationResources> {
    const store = useI18nStore.getState();
    const authStore = useAuthStore.getState();

    // Check if user is authenticated before trying API
    if (!authStore.isAuthenticated) {
      console.log('[i18n] User not authenticated, using local translations');
      store.setIsUsingFallback(true);
      return this.localRepo.loadTranslations(language, namespace);
    }

    try {
      // Fetch from API (fresh every time - per requirement)
      const allTranslations = await this.fetchFromApi(store.applicationType);

      // Cache in store and mark as NOT using fallback
      store.setTranslationCache(allTranslations);
      store.setIsUsingFallback(false);

      // Return translations for requested language
      const normalizedLang = language.toLowerCase();
      const langTranslations = allTranslations[normalizedLang] || {};

      // Filter and transform keys for the requested namespace
      // API returns keys as "namespace.key.path" but i18next expects "key.path" per namespace
      const namespacePrefix = `${namespace}.`;
      const namespacedTranslations: TranslationResources = {};

      for (const [key, value] of Object.entries(langTranslations)) {
        if (key.startsWith(namespacePrefix)) {
          // Strip the namespace prefix
          const keyWithoutNamespace = key.substring(namespacePrefix.length);
          namespacedTranslations[keyWithoutNamespace] = value;
        } else if (namespace === 'common' && !key.includes('.')) {
          // Include flat keys (without namespace prefix) in 'common' namespace
          // This handles Flutter's flat translation keys like 'text_session_tab_transaction_id'
          namespacedTranslations[key] = value;
        }
      }

    
      const localTranslations = await this.localRepo.loadTranslations(language, namespace);
      const merged: TranslationResources = { ...localTranslations, ...namespacedTranslations };

      if (import.meta.env.DEV) {
        console.log(
          `[i18n] API: Loaded ${Object.keys(namespacedTranslations).length} keys for ${language}:${namespace} (filtered from ${Object.keys(langTranslations).length} total, merged with ${Object.keys(localTranslations).length} local keys)`
        );
      }

      return merged;
    } catch (error) {
      console.warn('[i18n] API fetch failed, falling back to local files:', error);

      // Mark as using fallback
      store.setIsUsingFallback(true);

      // Fallback to local JSON files
      return this.localRepo.loadTranslations(language, namespace);
    }
  }

  /**
   * Fetch all translations from API
   * Returns cached promise if already fetching (prevents duplicate requests)
   */
  private async fetchFromApi(
    applicationType: ApplicationType
  ): Promise<Record<string, Record<string, string>>> {
    // If already fetching, return existing promise
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    this.fetchPromise = (async () => {
      try {
        const services = servicesContainer.getServices();
        const response = await services.getAllTranslationsUseCase.execute(applicationType);

        if (response.value && response.value.length > 0) {
          const transformed = transformToI18nResources(response.value);

          if (import.meta.env.DEV) {
            const langCount = Object.keys(transformed).length;
            const keyCount = response.value.length;
            console.log(
              `[i18n] Fetched ${keyCount} translation keys for ${langCount} languages (appType: ${applicationType})`
            );
          }

          return transformed;
        }

        throw new Error('No translations returned from API');
      } finally {
        // Clear promise so next call fetches fresh
        this.fetchPromise = null;
      }
    })();

    return this.fetchPromise;
  }

  /**
   * Get list of available languages
   */
  async getAvailableLanguages(): Promise<Language[]> {
    return SUPPORTED_LANGUAGES;
  }

  /**
   * Clear internal state (for testing or manual refresh)
   */
  clearCache(): void {
    this.fetchPromise = null;
    useI18nStore.getState().clearTranslationCache();
  }
}
